/**
 * @file src/offline/offlineConfig.js
 * @description The numbers and the switch for offline collection downloads.
 *
 * One constant per limit, because both are shown to the person as "used of allowed" and a number
 * on screen that disagrees with the one being enforced is worse than no number at all.
 *
 * On what the cap IS: it is a UX limit, not a protection. Everything a download stores lives in
 * this browser profile, and a cleared profile, a private window or a second browser all lose it.
 * So nothing may treat "it is downloaded" as an entitlement — the release policy that decided the
 * person may hear this track is upstream and stays upstream.
 */

/** How much a person may keep offline, across every downloaded collection. */
export const OFFLINE_MAX_BYTES = 5 * 1024 * 1024 * 1024;

/** How many collections may be downloaded at once. */
export const OFFLINE_MAX_COLLECTIONS = 10;

/**
 * What quality a download may be kept at, and how that reaches the bytes.
 *
 * Two qualities, chosen per collection. Compressed is the streaming MP3; lossless is the NORMALIZED
 * wav — 44.1 kHz, signed 16-bit, whatever channel count the source had — which is the same object a
 * listener on the HIGH audio preset already streams. The uploaded original is a different object
 * entirely and is never reachable here: the audio proxy serves `compressed` and `lossless` and
 * refuses anything else, so "download in lossless" cannot become "hand over the master".
 *
 * Quality is a property of a DOWNLOAD, not of a person and not of the platform. There is deliberately
 * no global setting: each collection is asked for at the quality it was asked for, so the expensive
 * choice is always one someone made about one collection, in front of its size.
 */
export const OFFLINE_QUALITY = {
  COMPRESSED: 'compressed',
  LOSSLESS: 'lossless',
};

/**
 * What a download is when nobody said otherwise — including every collection downloaded before this
 * existed, whose stored manifest carries no quality at all and whose bytes are all compressed.
 */
export const DEFAULT_OFFLINE_QUALITY = OFFLINE_QUALITY.COMPRESSED;

/** A stored or requested quality, as one of the two values. Anything unrecognised is compressed. */
export function normalizeOfflineQuality(value) {
  return String(value || '').toLowerCase() === OFFLINE_QUALITY.LOSSLESS
    ? OFFLINE_QUALITY.LOSSLESS
    : OFFLINE_QUALITY.COMPRESSED;
}

/**
 * The variant and asset format a quality names, in the vocabulary the cache key and the audio proxy
 * both already speak (`offlineKeys.js`, `/tracks/:id/audio?variant=`).
 *
 * The two always travel together — a compressed release is mp3, a lossless one pcm — and they are
 * returned together for the same reason `trackAudioKey` derives one from the other: a caller holding
 * only half of the pair can name lossless bytes as mp3.
 */
export function audioVariantForQuality(quality) {
  return normalizeOfflineQuality(quality) === OFFLINE_QUALITY.LOSSLESS
    ? { variant: 'lossless', assetFormat: 'pcm' }
    : { variant: 'compressed', assetFormat: 'mp3' };
}

/** Whether a hydrated track release actually HAS the variant a quality asks for. */
export function trackHasQuality(track, quality) {
  return normalizeOfflineQuality(quality) === OFFLINE_QUALITY.LOSSLESS
    ? Boolean(track?.hasAudioWAV)
    : Boolean(track?.hasAudioMP3);
}

import { hasOfflineDownloadIntent } from './offlineIntent.js';

const FLAG_PARAM = 'offlineDownloads';
const STORAGE_KEY = 'orbiters.offlineDownloads';

function readUrlFlag() {
  try {
    if (typeof window === 'undefined' || !window.location) return null;
    const params = new URLSearchParams(window.location.search || '');
    // Root's "Download for offline" action is itself behind root's switch, so arriving with the
    // intent IS the switch: the person was offered the feature and took it, and a studio that then
    // hid the control would be a door that opens onto a wall.
    if (hasOfflineDownloadIntent(params)) return true;
    const raw = params.get(FLAG_PARAM);
    if (raw === null) return null;
    return raw !== '0' && raw.toLowerCase() !== 'false';
  } catch {
    return null;
  }
}

function readStoredFlag() {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (raw === null || raw === undefined) return null;
    return raw === '1' || raw === 'true';
  } catch {
    return null;
  }
}

function readBuildFlag() {
  try {
    const raw = import.meta?.env?.VITE_OFFLINE_DOWNLOADS;
    if (raw === undefined || raw === null || raw === '') return null;
    return raw === '1' || String(raw).toLowerCase() === 'true';
  } catch {
    return null;
  }
}

/**
 * Whether the download surface is offered at all.
 *
 * Off by default while this is phase 1: the store and the download are real, but a collection is
 * only genuinely playable offline once the app itself boots without a network, which is phase 2.
 * Offering it before then would promise something the app cannot yet keep after a closed tab.
 *
 * The URL wins so a session can be opened straight into the feature; the stored answer is how a
 * `?offlineDownloads=1` visit survives the reload the person will inevitably do; the build flag is
 * how it turns on for everyone without a code change.
 */
export function isOfflineDownloadsEnabled() {
  const fromUrl = readUrlFlag();
  if (fromUrl !== null) {
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, fromUrl ? '1' : '0');
    } catch {
      // A session-only answer is still an answer; it just will not survive the reload.
    }
    return fromUrl;
  }
  const fromStorage = readStoredFlag();
  if (fromStorage !== null) return fromStorage;
  return readBuildFlag() === true;
}
